import L from 'leaflet';

const COLORS = {
  team:  '#25D366',
  field: '#1890ff',
  gk:    '#faad14',
};

// Escape HTML-special chars — profile_photo comes from user input via /community/players/me
// and Leaflet's L.divIcon sets innerHTML directly, so an unescaped `"` breaks out of the attribute.
function escapeAttr(s) {
  return String(s || '').replace(/[<>"'&]/g, c => ({
    '<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;','&':'&amp;'
  }[c]));
}

function pinSvg(color, glyph, badge) {
  const badgeHtml = badge && badge > 0
    ? `<g><circle cx="34" cy="8" r="8" fill="#fff" stroke="${color}" stroke-width="1.5"/>
        <text x="34" y="11" text-anchor="middle" font-size="10" font-weight="700" fill="${color}">${badge}</text></g>`
    : '';
  return `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
  <defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%">
    <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#000" flood-opacity="0.35"/>
  </filter></defs>
  <path d="M20 2 C10 2 3 9 3 19 c0 12 17 29 17 29 s17-17 17-29 c0-10-7-17-17-17z"
        fill="${color}" stroke="#fff" stroke-width="2" filter="url(#s)"/>
  <g transform="translate(10,7)" fill="#fff">${glyph}</g>
  ${badgeHtml}
</svg>`;
}

const TEAM_GLYPH   = `<path d="M10 0 L20 3 L20 10 C20 15 15 19 10 20 C5 19 0 15 0 10 L0 3 Z" fill="#fff"/>
                       <circle cx="10" cy="11" r="3.5" fill="${COLORS.team}"/>`;
const FIELD_GLYPH  = `<path d="M2 3 L8 0 L12 0 L18 3 L18 6 L14 6 L14 20 L6 20 L6 6 L2 6 Z" fill="#fff"/>`;
// Luva de goleiro: mao erguida (path do pan_tool do Material Icons, viewBox
// 24x24 escalado pra caber no viewbox 20x20 do glyph). Silhueta reconhecivel
// como mao com 4 dedos + polegar erguidos — parece luva de goleiro na defesa.
const GK_GLYPH     = `
  <g transform="scale(0.83) translate(-1 -1)" fill="#fff">
    <path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-0.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c0.22-0.19 0.49-0.29 0.79-0.29c0.22 0 0.42 0.06 0.6 0.16C3.73 13.46 8 15.91 8 15.91V4c0-0.83 0.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-0.83 0.67-1.5 1.5-1.5S15 0.67 15 1.5V11h1V2.5c0-0.83 0.67-1.5 1.5-1.5s1.5 0.67 1.5 1.5V11h1V5.5C20 4.67 20.67 4 21.5 4S23 4.67 23 5.5z"/>
  </g>
`;

export function teamIcon({ openGames = 0, logoUrl = null } = {}) {
  // Se time tem escudo, mostra a imagem dentro do pin verde (mais reconhecivel
  // que o icone generico). badge de openGames continua no canto superior.
  if (logoUrl) {
    const badge = openGames > 0
      ? `<div style="position:absolute;top:-4px;right:-4px;background:#fff;color:${COLORS.team};border-radius:50%;width:18px;height:18px;font-size:11px;font-weight:700;display:grid;place-items:center;border:2px solid ${COLORS.team};box-shadow:0 2px 4px rgba(0,0,0,.3);">${openGames}</div>`
      : '';
    const html = `
<div style="position:relative;width:44px;height:44px;">
  <div style="width:44px;height:44px;border-radius:12px;overflow:hidden;
              border:3px solid ${COLORS.team};box-shadow:0 3px 8px rgba(0,0,0,.35);background:#fff;">
    <img src="${escapeAttr(logoUrl)}" width="44" height="44" style="object-fit:cover;display:block;"
         onerror="this.parentElement.style.display='none';this.parentElement.nextElementSibling.style.display='block'"/>
  </div>
  <div style="display:none;">${pinSvg(COLORS.team, TEAM_GLYPH, openGames)}</div>
  ${badge}
</div>`;
    return L.divIcon({
      className: 'fz-map-icon', html,
      iconSize: [44, 44], iconAnchor: [22, 44], popupAnchor: [0, -40],
    });
  }
  return L.divIcon({
    className: 'fz-map-icon',
    html: pinSvg(COLORS.team, TEAM_GLYPH, openGames),
    iconSize: [40, 50], iconAnchor: [20, 48], popupAnchor: [0, -44],
  });
}

export function playerIcon({ isGoalkeeper = false, photoUrl = null, zoom = 0 } = {}) {
  if (photoUrl && zoom >= 14) {
    const color = isGoalkeeper ? COLORS.gk : COLORS.field;
    const html = `
<div style="width:44px;height:44px;border-radius:50%;overflow:hidden;
            border:3px solid ${color};box-shadow:0 2px 6px rgba(0,0,0,.35);background:#fff;">
  <img src="${escapeAttr(photoUrl)}" width="44" height="44" style="object-fit:cover;display:block;"
       onerror="this.style.display='none'"/>
</div>`;
    return L.divIcon({ className: 'fz-map-icon', html, iconSize: [44, 44], iconAnchor: [22, 22] });
  }
  const color = isGoalkeeper ? COLORS.gk : COLORS.field;
  const glyph = isGoalkeeper ? GK_GLYPH : FIELD_GLYPH;
  return L.divIcon({
    className: 'fz-map-icon',
    html: pinSvg(color, glyph, 0),
    iconSize: [40, 50], iconAnchor: [20, 48], popupAnchor: [0, -44],
  });
}
